import Highcharts from "highcharts";
import { HighchartsOptionsUtil } from "app/utils/highchartsOptionsUtil";
import { WorkElectricService } from "app/services/work/WorkElectricService";
import dayjs from "dayjs";
import { Constant } from "app/constants/constant";
import { ResponseResultCode } from "app/model/response-api";
import { useEffect, useState } from "react";
import { numberFormatter } from "app/utils/stringUtil";
import HighchartsReact from "highcharts-react-official";
import ArcButton, { ButtonIcon, ButtonType } from "app/components/arc/ArcButton";
import { useNavigate } from "react-router-dom";
import DashboardLoading from "app/pages/db/modules/DashboardLoading";
import useInterval from "app/hooks/use-interval";

const defaultOption: Highcharts.Options = {
  chart: {
    type: "pie",
    backgroundColor: "transparent",
    height: 170,
    margin: [-10, 130, 0, -30],
    spacing: [0, 0, 0, 0],
  },
  legend: {
    layout: "horizontal",
    verticalAlign: "middle",
    align: "right",
    symbolHeight: 0,
    symbolWidth: 0,
    x: 65,
    squareSymbol: false,
    useHTML: true,
    labelFormatter: function () {
      return (`
        <span style="display: flex; width:220px; justify-content: space-between; align-items: center; padding: 4px 0;">
          <span style="width: 73%; text-align:left;display:flex;align-items:center;">
            <em style="display:inline-block; min-width:20px; height:20px; border-radius: 50%; border: 5px solid ${this.color};clip-path: polygon(0 0, 50% 0, 50% 100%, 0 100%);"></em>
            <span style="font-size:.7rem;white-space:initial;">
              ${this.name}
            </span>
          </span>
        </span>`
      );
    },
  },
  tooltip: {
    formatter: function(this:any):any {
      return `<div>
            <span style="color:${this.color}">●</span>
            <span style='font-weight:500;'>${this.point.name}:&nbsp;</span>
            <span style='font-weight:700;'>${numberFormatter(this.point.y, 2)}</span> MWh
          </div>`;
    },
  },
  plotOptions: {
    pie: {
      allowPointSelect: true,
      cursor: "pointer",
      showInLegend: true,
      innerSize: "73%",
      size: "80%",
      borderWidth: 0.5,
      borderRadius: 0,
      dataLabels: {
        enabled: false,
      },
    },
  },
  colors: ["#545CC1", "#85A0ED", "#3C92FF", "#3CC4FF"],
  series: [],
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 300,
        },
        chartOptions: {
          chart: {
            height: 200,
            margin: [-5, -10, 90, -10],
          },
          legend: {
            layout: "horizontal",
            verticalAlign: "bottom",
            align: "center",
            symbolHeight: 0,
            symbolWidth: 0,
            x: 40,
            y:-20,
            squareSymbol: false,
            useHTML: true,
            labelFormatter: function () {
              return (`
                <span style="display: flex; width:220px; justify-content: space-between; align-items: center; padding: 4px 0;">
                  <span style="width: 73%; text-align:left;display:flex;align-items:center;">
                    <em style="display:inline-block; min-width:20px; height:20px; border-radius: 50%; border: 5px solid ${this.color};clip-path: polygon(0 0, 50% 0, 50% 100%, 0 100%);"></em>
                    <span style="font-size:.7rem;white-space:initial;">
                      ${this.name}
                    </span>
                  </span>
                </span>`
              );
            },
          },
          subtitle: {
            align: 'center',
            verticalAlign: 'top',
            x: 0,
            y: 52,
          },
          plotOptions: {
            pie: {
              size: '110%'
            }
          }
        }
      }
    ]
  }
};

export default function DbBzkndEnrg() {
  Highcharts.setOptions({
    ...HighchartsOptionsUtil.locale.kr.globalOptions,
    title: { text: '' },
    exporting: {enabled: false},
    credits: {enabled: false},
    tooltip: {shared: true, headerFormat: ''},
    accessibility: {enabled: false},
  });

  const navigate = useNavigate();

  const subtitleTemplate = (totPwr: number) => {
    return `
      <div style="text-align: center;">
        <div style="color: #444; font-size:.8rem; font-weight: bold;">
          ${numberFormatter(Math.round(totPwr))}
        </div>
        <div style="color: #666; font-size: 14px;">MWh</div>
      </div>
    `;
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [option, setOption] = useState<Highcharts.Options>(defaultOption);
  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    fetch();
  }, []);

  useInterval(() => {
    fetch();
  }, 600000);

  function fetch() {
    const searchType: string = 'YEAR';
    const fromDt: string = dayjs().format(Constant.FORMAT_YMD_ALT);
    const search = { searchType: searchType, fromDt: fromDt };

    WorkElectricService.getInstance().getPwrUseBzknd(search)
    .then(response => {
      if (response && ResponseResultCode.Y === response.resultCode) {
        if (response.data && response.data.length > 0) {
          const temp = response.data.slice(0,6);
          let series: any[] = [];
          let bottom: any[] = [];
          let totPwrUse: number = 0;
          temp.forEach(item => {
            series.push({ name: item.bzkndNm, y: item.pwrUse / 1000 ?? 0 });
            bottom.push({ name: item.bzkndNm, mwh: item.pwrUse / 1000 ?? 0, corp: item.corpCnt ?? 0 });
            totPwrUse += parseFloat(((item.pwrUse ?? 0) / 1000).toFixed(2));
          });

          setData(bottom);
          setOption({
            ...defaultOption,
            subtitle: {
              useHTML: true,
              align: 'center',
              verticalAlign: 'middle',
              x: -80,
              text: subtitleTemplate(totPwrUse),
            },
            series: [{ type: 'pie', data: series }],
            plotOptions:{
              pie:{
                borderWidth:0
              }
            }
          });
        } else {
          setOption({ ...defaultOption });
          setData([]);
        }
      } else {
        setOption({...defaultOption});
        setData([]);
      }
    })
    .catch(() => {
      setOption({...defaultOption});
      setData([]);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }

  return (
    <div className="cont">
      <DashboardLoading isLoading={isLoading} />
      <h2 className="titT2 bl">
        <span className="mgr10">업종별 에너지 현황</span>
        <em style={{fontSize:"0.7rem", fontWeight: "lighter"}}>(상위 6개)</em>
      </h2>
      <div className="chart">
        {option && (<HighchartsReact highcharts={Highcharts} options={option} /> )}
      </div>
      <div className="txt-flex2">
        {(data && data.length > 0) && data.map(item => (
          <div key={`db-bzknd-enrg-txt-grid-${item.name}`}>
            <p className="tit">{item.name} ({item.corp})</p>
            <strong>
              <em>{numberFormatter(item.mwh ?? 0, 2)}</em>MWh
            </strong>
          </div>
        ))}
      </div>
      <ArcButton
        type={ButtonType.custom}
        className="more-btn"
        icon={ButtonIcon.plus}
        hasIcon
        onClick={() => navigate('/work/electric/bzknd')}
      />
    </div>
  );
}